// Single typed access point for environment variables.
// Never read import.meta.env directly outside this file.

function required(name, value, fallback) {
  if (value === undefined || value === '') {
    if (fallback !== undefined) {
      return fallback;
    }
    throw new Error(`Missing required env var: ${name}`);
  }
  return value;
}

const raw = import.meta.env;
const mode = raw.VITE_ENV ?? raw.MODE ?? 'development';
const isTest = mode === 'test';
const cashfreeMode =
  raw.VITE_CASHFREE_MODE === 'production' || (!raw.VITE_CASHFREE_MODE && mode === 'production')
    ? 'production'
    : 'sandbox';
const razorpayMode =
  raw.VITE_RAZORPAY_MODE === 'production' || (!raw.VITE_RAZORPAY_MODE && mode === 'production')
    ? 'production'
    : 'sandbox';
const razorpayCurrency = String(raw.VITE_RAZORPAY_CURRENCY || 'INR').toUpperCase();
// const cashfreeMode = 'sandbox';

export const env = {
  apiBaseUrl: required(
    'VITE_API_BASE_URL',
    raw.VITE_API_BASE_URL,
    isTest ? 'http://localhost' : undefined,
  ),
  defaultIndustry: raw.VITE_DEFAULT_INDUSTRY ?? 'default',
  environment: mode,
  sentryDsn: raw.VITE_SENTRY_DSN ?? null,
  isProduction: mode === 'production',
  isDevelopment: mode === 'development',

  // Per-industry tenant overrides for local dev. Production gets these from
  // the industry config. Setting them in .env.local lets a developer hit a
  // specific account/channel/location without editing config files.
  accountIdOverride: raw.VITE_ACCOUNT_ID || null,
  channelIdOverride: raw.VITE_CHANNEL_ID || null,
  locationIdOverride: raw.VITE_LOCATION_ID || null,

  // Dev-only seed for the auth store. Paste the admin-issued access /
  // refresh tokens into .env.local while the customer-side flow is still
  // being validated against the dev backend. Strips out at production
  // build (VITE_ENV !== 'development') as a guardrail.
  devAccessToken: mode === 'development' ? raw.VITE_DEV_ACCESS_TOKEN || null : null,
  devRefreshToken: mode === 'development' ? raw.VITE_DEV_REFRESH_TOKEN || null : null,

  // Which storefront domain the tenant is resolved from, overriding the browser
  // host. Local dev serves from localhost and QA from a preview host, neither of
  // which is a registered storefront, so /public/store/resolve 404s and the app
  // renders "Account Not Found". Pointing this at a real domain makes `pnpm dev`
  // boot straight into that merchant.
  //
  // Stripped at production build, where the browser host IS the tenant: a
  // deployed storefront must never be pinnable to another merchant's account.
  // Native builds ignore it — they resolve from merchant.json (ADR 0005).
  storefrontDomain: mode === 'production' ? '' : raw.VITE_STOREFRONT_DOMAIN || null,

  // Per-tenant API key. When set, requests use
  // `Authorization: ApiKey <key>` instead of the customer Bearer token.
  apiKey: raw.VITE_API_KEY || null,

  // Public store-resolve key sent as `x-api-key` on the resolve endpoint.
  publicResolveApiKey: raw.VITE_PUBLIC_RESOLVE_API_KEY || raw.VITE_API_KEY || null,

  // Cashfree SDK mode. Defaults to sandbox in dev and production in production builds.
  cashfreeMode,

  // Razorpay has no SDK mode flag; the key decides test vs live. This guard
  // keeps dev/QA on rzp_test_* keys and production on rzp_live_* keys.
  razorpayMode,
  razorpayCurrency,

  // Public https origin of the deployed storefront. On web this is just
  // window.location.origin, but the native shell runs on capacitor://localhost
  // (iOS) / https://localhost (Android), which a payment gateway can neither
  // redirect to nor validate. Native builds therefore point gateway return_urls
  // at this origin, which serves the Universal Link / App Link association
  // files that hand control back to the app. See ADR 0004.
  publicWebOrigin: raw.VITE_PUBLIC_WEB_ORIGIN || null,
};
